import type { JWTVFreeTrialPlacement } from '@/components/experiments/JWTVFreeTrialExp/helper';
import { computed } from 'vue';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import type { ClickoutUrl } from '@/helpers/clickout-helper';
import { MonetizationType } from '@/@types/graphql-types';
import { isJwtvTvodOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { openJwTvAvodModal } from '@/features/jwtv-avod/helpers/jwtv-avod-helper';
import { isJwtvSvodOffer, openJwTvSvodModal } from '@/features/jwtv-savod/helpers/jwtv-savod-helper';
import { isJWTVOffer, JWTV_PROVIDER_CONFIG } from '@/features/jwtv/config';
import { type MaybeRefOrGetter, toValue } from '@vueuse/core';
import type { PlayerModalTitle, PlayerModalOffer } from '@/features/title-player/types';
import type { SnowplowContext } from '@/helpers/tracking/providers';

type OfferWithPackage = {
	package: {
		shortName: string;
		technicalName: string;
	};
	monetizationType: MonetizationType;
	streamUrl?: string | null;
};

const JWTV_SHORT_NAMES = [...new Set(JWTV_PROVIDER_CONFIG.map(c => c.shortName))];
const MODAL_PROVIDER_SHORT_NAMES = [...JWTV_SHORT_NAMES, 'otp', 'pxp', 'brp'];

const playerModalByProvider: Record<string, { cmp: () => Promise<unknown> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
	brollieplayer: {
		cmp: () => import('@/ui-components/video-player-modals/BrolliePlayerModal.vue'),
	},
};

export function useTitlePlayerModal(shortName: MaybeRefOrGetter<string>) {
	const { openRentalModal } = useRentalModal();

	function openThirdPartyPlayerModal({
		offer,
		title,
		additionalContexts,
		onClose,
	}: {
		offer: OfferWithPackage;
		title?: PlayerModalTitle;
		additionalContexts: SnowplowContext[];
		onClose?: () => void;
	}): void {
		const modalData = playerModalByProvider[offer.package.technicalName];
		ModalHelper.openModal(
			modalData.cmp,
			{
				offer,
				title,
				additionalContexts,
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[],
			onClose
		);
	}

	async function openPlayerModal({
		offer,
		title,
		clickoutUrl,
		playerContexts,
		onClose,
		rentalOptions,
		placement,
	}: {
		offer: PlayerModalOffer;
		title?: PlayerModalTitle;
		clickoutUrl?: ClickoutUrl;
		playerContexts?: SnowplowContext[];
		onClose?: () => void;
		rentalOptions?: {
			overrideRedirectUrl?: boolean;
		};
		// EXP_JW_PLUS_FREE_TRIAL
		placement?: JWTVFreeTrialPlacement;
	}): Promise<void> {
		if (!checkIfOfferIsModalProvider(offer.package.shortName)) {
			return;
		}

		if (clickoutUrl) {
			trackClickout(clickoutUrl);
		}

		const additionalContexts = clickoutUrl ? getClickoutUrlContextsArray(clickoutUrl) : playerContexts ?? [];

		if (isJwtvTvodOffer(offer)) {
			openRentalModal({
				offer,
				title: title!,
				additionalContexts,
				onClose,
				overrideRedirectUrl: rentalOptions?.overrideRedirectUrl ?? true,
			});
			return;
		}

		if (isJwtvSvodOffer(offer)) {
			await openJwTvSvodModal({
				modalProps: { offer, title, additionalContexts },
				onClose,
			});
			return;
		}

		if (isJWTVOffer(offer)) {
			await openJwTvAvodModal({
				modalProps: { offer, title, additionalContexts, placement },
				onClose,
			});
		} else {
			openThirdPartyPlayerModal({ offer, title, additionalContexts, onClose });
		}
	}

	function checkIfOfferIsModalProvider(shortName: string): boolean {
		return MODAL_PROVIDER_SHORT_NAMES.includes(shortName);
	}

	const isModalProvider = computed(() => checkIfOfferIsModalProvider(toValue(shortName)));

	return { isModalProvider, openPlayerModal, checkIfOfferIsModalProvider };
}
